import { FormEvent, useState } from 'react';
import { api } from '../lib/api';
import { FormField } from './FormField';
import { AttendeeSelect, AttendeeState } from './AttendeeSelect';
import type { User, FamilyRole, DayOfWeek, ActivityCategory } from '@kinapp/shared';

interface ActivityFormProps {
  familyId: string;
  dependents: Array<{ id: string; name: string }>;
  familyMembers: Array<{ user: User; role: FamilyRole }>;
  onSuccess: () => void;
  onCancel: () => void;
  initialDayOfWeek?: DayOfWeek;
  initialStartTime?: string;
}

const DAYS: { value: DayOfWeek; label: string }[] = [
  { value: 'MONDAY' as DayOfWeek, label: 'Mon' },
  { value: 'TUESDAY' as DayOfWeek, label: 'Tue' },
  { value: 'WEDNESDAY' as DayOfWeek, label: 'Wed' },
  { value: 'THURSDAY' as DayOfWeek, label: 'Thu' },
  { value: 'FRIDAY' as DayOfWeek, label: 'Fri' },
  { value: 'SATURDAY' as DayOfWeek, label: 'Sat' },
  { value: 'SUNDAY' as DayOfWeek, label: 'Sun' },
];

const CATEGORIES: { value: ActivityCategory; label: string }[] = [
  { value: 'SPORT' as ActivityCategory, label: 'Sport' },
  { value: 'MUSIC' as ActivityCategory, label: 'Music' },
  { value: 'ACADEMIC' as ActivityCategory, label: 'Academic' },
  { value: 'SOCIAL' as ActivityCategory, label: 'Social' },
  { value: 'MEDICAL' as ActivityCategory, label: 'Medical' },
  { value: 'TRANSPORT' as ActivityCategory, label: 'Transport' },
  { value: 'OTHER' as ActivityCategory, label: 'Other' },
];

interface SessionInput {
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  responsibleId: string;
}

export function ActivityForm({ familyId, dependents, familyMembers, onSuccess, onCancel, initialDayOfWeek, initialStartTime }: ActivityFormProps) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<ActivityCategory>('SPORT' as ActivityCategory);
  const [venue, setVenue] = useState('');
  const [notes, setNotes] = useState('');
  const [sessions, setSessions] = useState<SessionInput[]>(() => {
    const startTime = initialStartTime ?? '16:00';
    const [h, m] = startTime.split(':').map(Number);
    const endTotal = h * 60 + m + 60;
    const endTime = `${String(Math.floor(endTotal / 60) % 24).padStart(2, '0')}:${String(endTotal % 60).padStart(2, '0')}`;
    return [{ dayOfWeek: (initialDayOfWeek ?? 'MONDAY') as DayOfWeek, startTime, endTime, responsibleId: '' }];
  });
  const [attendees, setAttendees] = useState<AttendeeState>({
    selectedUserIds: [],
    selectedDepIds: dependents.map((d) => d.id),
    externalGuests: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addSession = () => {
    setSessions([...sessions, { dayOfWeek: 'MONDAY' as DayOfWeek, startTime: '16:00', endTime: '17:00', responsibleId: '' }]);
  };

  const removeSession = (index: number) => {
    setSessions(sessions.filter((_, i) => i !== index));
  };

  const updateSession = (index: number, field: keyof SessionInput, value: string) => {
    setSessions(sessions.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (sessions.length === 0) {
      setError('At least one session is required');
      return;
    }

    setLoading(true);
    try {
      await api.createScheduledActivity(familyId, {
        dependentId: attendees.selectedDepIds[0],
        dependentIds: attendees.selectedDepIds,
        attendingUserIds: attendees.selectedUserIds,
        externalGuests: attendees.externalGuests.length ? attendees.externalGuests : undefined,
        name: name.trim(),
        category,
        venue: venue.trim() || undefined,
        notes: notes.trim() || undefined,
        sessions: sessions.map((s) => ({
          dayOfWeek: s.dayOfWeek,
          startTime: s.startTime,
          endTime: s.endTime,
          responsibleId: s.responsibleId || undefined,
        })),
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create activity');
    } finally {
      setLoading(false);
    }
  };

  const parents = familyMembers.filter((m) => m.role === 'OWNER' || m.role === 'MEMBER');

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <AttendeeSelect
        dependents={dependents}
        familyMembers={familyMembers}
        value={attendees}
        onChange={setAttendees}
      />

      <FormField label="Activity name" id="activity-name" required>
        <input
          type="text"
          id="activity-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input"
          placeholder="e.g., Piano Lesson, Soccer Practice"
          required
        />
      </FormField>

      <FormField label="Category" id="activity-category" required>
        <select
          id="activity-category"
          value={category}
          onChange={(e) => setCategory(e.target.value as ActivityCategory)}
          className="input"
        >
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </FormField>

      <FormField label="Venue" id="activity-venue">
        <input
          type="text"
          id="activity-venue"
          value={venue}
          onChange={(e) => setVenue(e.target.value)}
          className="input"
          placeholder="e.g., Lincoln Park Sports Center"
        />
      </FormField>

      {/* Sessions */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="label">Sessions</label>
          <button type="button" onClick={addSession} className="text-sm text-primary-600 hover:text-primary-700">
            + Add session
          </button>
        </div>

        <div className="space-y-3">
          {sessions.map((session, i) => (
            <div key={i} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <select
                  value={session.dayOfWeek}
                  onChange={(e) => updateSession(i, 'dayOfWeek', e.target.value)}
                  className="input flex-1"
                >
                  {DAYS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                {sessions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSession(i)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="flex gap-2 mb-2">
                <input
                  type="time"
                  value={session.startTime}
                  onChange={(e) => updateSession(i, 'startTime', e.target.value)}
                  className="input flex-1"
                />
                <span className="self-center text-gray-500">to</span>
                <input
                  type="time"
                  value={session.endTime}
                  onChange={(e) => updateSession(i, 'endTime', e.target.value)}
                  className="input flex-1"
                />
              </div>
              <select
                value={session.responsibleId}
                onChange={(e) => updateSession(i, 'responsibleId', e.target.value)}
                className="input w-full"
              >
                <option value="">Responsible: Auto (from custody)</option>
                {parents.map(({ user }) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      <FormField label="Notes" id="activity-notes">
        <textarea
          id="activity-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="input"
          rows={2}
          placeholder="Any additional notes..."
        />
      </FormField>

      <div className="flex gap-3 mt-8">
        <button type="button" onClick={onCancel} className="flex-1 btn btn-secondary" disabled={loading}>
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create activity'}
        </button>
      </div>
    </form>
  );
}
